import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryService } from "../services/query-service";
import type { UpdateProfileRequest } from "../models/api-types";
import useAuthStore from "@/store/use-auth-store";
import { QueryKeys } from "../config/query-keys";

/**
 * Hook to update user profile
 * @returns Mutation result with update profile functionality
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { setUser, refreshUserData } = useAuthStore();

  return useMutation({
    mutationFn: (profileData: UpdateProfileRequest) =>
      queryService.updateProfile(profileData),

    onSuccess: async (response, variables) => {
      // Update user in auth store with merged data for immediate UI consistency
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        setUser({
          ...currentUser,
          ...(response.data as any),
          ...variables,
        } as any);
      } else if (response.data) {
        setUser(response.data as any);
      }

      // Force re-sync from /me to ensure backend source-of-truth is applied
      await refreshUserData(true);

      // Invalidate user-related queries
      queryClient.invalidateQueries({
        queryKey: ["user"],
      });

      // Region değişmişse pricing cache'ini de sıfırla.
      // Backend, /pricing/get-all-pricing endpoint'inde kullanıcının
      // profilindeki region'a göre para birimi döndürdüğünden, profil
      // güncellemesi sonrasında eski fiyatların gösterilmemesi için
      // tüm pricing sorgularını invalidate et.
      if (variables.region !== undefined) {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.PRICING.all,
        });
      }
    },

    onError: (error) => {
      console.error("❌ Failed to update profile:", error);
    },
  });
};
